import {
    applyActionCode,
    confirmPasswordReset,
    createUserWithEmailAndPassword,
    EmailAuthProvider,
    fetchSignInMethodsForEmail,
    getIdToken,
    reauthenticateWithCredential,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signOut,
    updatePassword,
    updateProfile,
    verifyPasswordResetCode,
} from 'firebase/auth';
import {
    addDoc,
    collection,
    doc,
    DocumentData,
    getDoc,
    getDocs,
    query,
    setDoc,
    UpdateData,
    updateDoc,
} from 'firebase/firestore';
import * as jose from 'jose';

import { IChild, IUser } from '@customTypes/user';
import { getRoleByRoleCode } from 'external/firebase/firestore/collections/roles';
import { updateParentChild } from 'external/firebase/firestore/collections/users';
import { auth, BASE_CLOUD_FUNCTION_URL, db } from 'external/firebase/index';
import { PATH_AUTH } from 'routes/paths';

import { automaticallyAssignSubscription } from './db';
import { searchStripeCustomer } from './stripe';
import { formatErrorMessage } from './utils';

export const login = async (email: string, password: string) => {
    try {
        await signInWithEmailAndPassword(auth, email, password);
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const sendConfirmationEmail = async () => {
    if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
    }
};

export const registerChild = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    parentId: string,
) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const docRef = doc(db, 'users', userCredential.user.uid);

        const existingChildDoc = await getDoc(docRef);
        const existingGroups = existingChildDoc.exists() ? existingChildDoc.data()?.groups : undefined;
        const userData = {
            email: userCredential.user.email,
            firstName,
            lastName,
            role: 'child',
            parent: parentId,
            emailVerified: false,
            ...(existingGroups && { groups: existingGroups }),
        };
        await setDoc(docRef, userData, { merge: true });
        await Promise.all([
            updateProfile(userCredential.user, {
                displayName: `${firstName} ${lastName}`,
            }),
            sendConfirmationEmail(),
            updateParentChild(userCredential.user.email as string, firstName, lastName, 'registered', parentId),
            automaticallyAssignSubscription(userCredential.user.uid, userCredential.user.email as string, parentId),
        ]);
        return { ok: true } as const;
    } catch (error) {
        console.error(error);
        return formatErrorMessage(error);
    }
};

export const registerParent = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
        const stripeCustomer = await searchStripeCustomer(email);
        if (!stripeCustomer.id) {
            throw new Error('auth/no-stripe-customer');
        }
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const docRef = doc(db, 'users', userCredential.user.uid);
        const userData = {
            email: userCredential.user.email,
            firstName,
            lastName,
            role: 'parent',
            stripeId: stripeCustomer.id,
            emailVerified: false,
        };
        await Promise.all([
            setDoc(docRef, userData, { merge: true }),
            updateProfile(userCredential.user, {
                displayName: `${firstName} ${lastName}`,
            }),
            sendConfirmationEmail(),
        ]);
        return { ok: true } as const;
    } catch (error) {
        console.error(error);
        return formatErrorMessage(error);
    }
};

export const registerStaff = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const docRef = doc(db, 'users', userCredential.user.uid);
        const userData = {
            email: userCredential.user.email,
            firstName,
            lastName,
            emailVerified: false,
        };
        await Promise.all([
            setDoc(docRef, userData, { merge: true }),
            updateProfile(userCredential.user, {
                displayName: `${firstName} ${lastName}`,
            }),
            sendConfirmationEmail(),
        ]);
        return { ok: true } as const;
    } catch (error) {
        console.error(error);
        return formatErrorMessage(error);
    }
};

export const updateUser = async (firstName: string, lastName: string, photoURL: string) => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/user-not-found');
        }
        const docRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(docRef, { firstName, lastName } as UpdateData<never>);
        await updateProfile(auth.currentUser, {
            displayName: `${firstName} ${lastName}`,
            photoURL,
        });
        return { ok: true } as const;
    } catch (error) {
        console.log(error);
        return formatErrorMessage(error);
    }
};

export const updateCredentials = async (oldPassword: string, newPassword: string) => {
    try {
        if (!auth.currentUser || !auth.currentUser.email) {
            throw new Error('auth/user-not-found');
        }
        const emailCredential = EmailAuthProvider.credential(auth.currentUser.email, oldPassword);
        await reauthenticateWithCredential(auth.currentUser, emailCredential);
        await updatePassword(auth.currentUser, newPassword);
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const setUserRole = async (roleCode: string) => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/user-not-found');
        }
        const role = await getRoleByRoleCode(roleCode);
        if (role?.error) {
            throw new Error(role.error);
        }
        if (!role) {
            throw new Error('auth/invalid-verification-code');
        }
        const docRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(docRef, {
            role,
        });
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const logout = async () => {
    await signOut(auth);
};

export const refreshToken = async () => {
    if (auth.currentUser) {
        // reload() refreshes emailVerified on the cached User object after the
        // user confirms their email; getIdToken(true) only refreshes the token
        // claims and leaves user.emailVerified stale.
        await auth.currentUser.reload();
        await getIdToken(auth.currentUser, true);
    }
};

export const resetPassword = async (email: string, userResult?: IUser | { ok: false; error: { code: string } }) => {
    try {
        if (userResult && 'id' in userResult && userResult.role === 'child') {
            return await resetChildPassword(email);
        }

        try {
            const signInMethods = await fetchSignInMethodsForEmail(auth, email);
            if (signInMethods.length === 0) {
                return { ok: true, email } as const;
            }
        } catch (userCheckError) {
            console.error(userCheckError);
        }

        const actionCodeSettings = {
            url: `${window.location.origin}/auth/user-management`,
            handleCodeInApp: false,
        };

        await sendPasswordResetEmail(auth, email, actionCodeSettings);

        return { ok: true, email } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const resetChildPassword = async (childEmail: string) => {
    try {
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/usersManagement/admin/initiateChildPasswordReset`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ childEmail }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to initiate child password reset');
        }

        const data = await response.json();
        return { ok: true, email: data.parentEmail } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const checkPasswordResetCode = async actionCode => {
    try {
        await verifyPasswordResetCode(auth, actionCode);
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const handleResetPassword = async (actionCode, newPassword) => {
    try {
        await confirmPasswordReset(auth, actionCode, newPassword);
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const handleVerifyEmail = async actionCode => {
    try {
        await applyActionCode(auth, actionCode);
        return { ok: true } as const;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getModuleLesson = async (module: string, lesson: string) => {
    try {
        // TODO: Change "topics" to "lessons" once DB migrations is done
        const docRef = doc(db, 'modules', module, 'topics', lesson);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data();
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getUserSubscriptions = async () => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }
    const subscriptions: DocumentData[] = [];
    const subscriptionsEnchanted: DocumentData = [];
    const q = query(collection(db, 'users', auth.currentUser.uid, 'subscriptions'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        subscriptions.push({ id: docSnap.id, ...docSnap.data() });
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const subscription of subscriptions) {
        // eslint-disable-next-line no-await-in-loop
        const product = (await getDoc(subscription.product)).data();
        // eslint-disable-next-line no-await-in-loop
        const price = (await getDoc(subscription.price)).data();
        subscriptionsEnchanted.push({ ...subscription, product, price });
    }
    return subscriptionsEnchanted;
};

export const sendInvitationEmail = async (parentId: string, children: IChild) => {
    const secret = jose.base64url.decode('zH4NRP1HMALxxCFnRZABFA7GOJtzU_gIj02alfL1lvI');
    const jwt = await new jose.EncryptJWT({
        ...children,
        role: 'child',
        parent: parentId,
    } as unknown as jose.JWTPayload)
        .setProtectedHeader({ alg: 'dir', enc: 'A128CBC-HS256' })
        .setIssuedAt()
        .setExpirationTime('24h')
        .encrypt(secret);
    await addDoc(collection(db, 'mail'), {
        to: [children.email],
        message: {
            subject: 'Jūs buvote pakviesti prisijungti prie CodeAcademy Kids!',
            text: `
                Labas,
                
                Tavo tėtis/mama tik ką nusipirko tau prenumeratą CodeAcademy Kids platformai! Tai nuostabi galimybė išmokti programuoti ir atrasti naujus žinių horizontus.
                
                Mes labai laukiame, kol prisijungsi ir pradėsi savo nuotykius pasauliu programuojant! Čia yra informacija, kuri padės tau pradėti:
                
                Paspausk šią nuorodą: ${window.location.origin}${PATH_AUTH.register}?code=${jwt}
                Tai tave nukreips į CodeAcademy Kids prisijungimo puslapį.
                
                Jei turėsi kokių nors klausimų ar reikės pagalbos, prašyk tėčio/mamos pagalbos arba susisiek su mumis. Mūsų komanda mielai padės tau pradėti ir pasiūlys patarimų bei pagalbos.
                
                Mėgaukis mokymusi ir sėkmės atrandant naujas žinias programuojant su CodeAcademy Kids!
                
                Su geriausiais linkėjimais,
                CodeAcademy Kids komanda
            `,
            html: `
                <p>Labas,</p><br>
                    
                <p>Tavo tėtis/mama tik ką nusipirko tau prenumeratą CodeAcademy Kids platformai! Tai nuostabi galimybė išmokti programuoti ir atrasti naujus žinių horizontus.</p><br>
                
                <p>Mes labai laukiame, kol prisijungsi ir pradėsi savo nuotykius pasauliu programuojant! Čia yra informacija, kuri padės tau pradėti:</p><br>
                
                <p>Paspausk šią nuorodą: <a href='${window.location.origin}${PATH_AUTH.register}?code=${jwt}' target="_blank">${window.location.origin}${PATH_AUTH.register}?code=${jwt}</a></p><br>
                <p>Tai tave nukreips į CodeAcademy Kids prisijungimo puslapį.</p><br>
                
                <p>Jei turėsi kokių nors klausimų ar reikės pagalbos, prašyk tėčio/mamos pagalbos arba <a href="https://www.codeacademykids.com/kontaktai/" target="_blank">susisiek su mumis</a>. Mūsų komanda mielai padės tau pradėti ir pasiūlys patarimų bei pagalbos.</p><br>
                
                <p>Mėgaukis mokymusi ir sėkmės atrandant naujas žinias programuojant su CodeAcademy Kids!</p><br>
                
                <p>Su geriausiais linkėjimais,</p>
                <p>CodeAcademy Kids komanda</p>
                `,
        },
    });
};
